import React from 'react';
import PropTypes from 'prop-types';
import { getColorForValue } from '../../Utils/ColorUtils';
import { FaStar } from 'react-icons/fa';
import CustomTooltip from '../../Utils/CustomTooltip';

// Skill progress bar component
const SkillProgressBar = ({ skill, grade, rationale, source, importance, isTop, showSource = true }) => {
  // Check if this is a top skill
  const isTopSkill = Boolean(isTop);
  // Ensure grade is between 0 and 10
  const numericGrade = Math.min(10, Math.max(0, grade));
  
  // Calculate width percentage
  const widthPercentage = (numericGrade / 10) * 100;
  
  // Get color based on grade
  const barColor = getColorForValue(numericGrade);
  
  return (
    <div className="mb-6">
      <div className="flex justify-between items-center mb-1">
        <div className="flex items-center">
          <h5 className="text-lg font-medium text-gray-800 flex items-center">
            {isTopSkill && (
              <CustomTooltip content="Top 5 skills" position='right'>
                <span className="inline-block mr-1">
                  <FaStar className="text-yellow-500" />
                </span>
              </CustomTooltip>
            )}
            {skill}
          </h5>
          {importance && (
            <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
              importance === "Required" 
                ? "bg-blue-100 text-blue-800" 
                : "bg-gray-100 text-gray-800"
            }`}>
              {importance}
            </span>
          )}
        </div>
        <div className="flex items-center">
          <span className="text-gray-700 font-medium mr-2">{numericGrade}</span>
          {showSource && source && (
            <CustomTooltip content={Array.isArray(source) ? source.join(', ') : source} position="left">
              <span className="text-xs text-blue-600 cursor-help hover:text-blue-800 flex items-center">
                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Source
              </span>
            </CustomTooltip>
          )}
        </div>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
        <div 
          className="h-4 rounded-full" 
          style={{ 
            width: `${widthPercentage}%`,
            backgroundColor: barColor
          }}
        ></div>
      </div>
      <p className="text-sm text-gray-600">{rationale}</p>
    </div>
  );
};

const HardSkillsList = ({ skills, showSources = true }) => {
  if (!skills || !Array.isArray(skills) || skills.length === 0) {
    return <p className="text-gray-600">No hard skills data available.</p>;
  }

  return (
    <div>
      {skills.map((skill, index) => (
        <SkillProgressBar
          key={index}
          skill={skill.skill}
          grade={skill.grade}
          rationale={skill.rationale}
          source={skill.source}
          showSource={showSources}
          importance={skill.importance}
          isTop={skill.top5}
        />
      ))}
    </div>
  );
};

HardSkillsList.propTypes = {
  skills: PropTypes.array,
  showSources: PropTypes.bool
};

export default HardSkillsList;
